import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { getSiteConfig } from '@shared/config';
import { createDebug } from '@shared/debug';
import { useEffect, useEffectEvent, useRef } from 'react';
import { useGreenIdComplete } from '../steps/GreenId/hooks/useGreenIdComplete';
import type { KycVerificationMethod, OpenKycDialogOptions, StartVerificationFlowResponse } from '../types';

const debug = createDebug({ isEnabled: true });

type UseKycStartMethodOptions = {
  isKycDialogOpen: boolean;
  startMethod: OpenKycDialogOptions['startMethod'];
  /** Starts the flow of the method with this id, resolving to its response or `null` when it failed. */
  startFlow: (flowId: string) => Promise<StartVerificationFlowResponse | null>;
  /** Drops the response of the current flow so its step does not render a stale one. */
  clearFlow: () => void;
  setHasError: (hasError: boolean) => void;
};

/**
 * Runs the method requested through `startMethod` (e.g. GreenID) straight away, skipping the method selection.
 *
 * Behaviour overview:
 * - Resolves the dialog step that runs the method on the current site (`KYC_VERIFICATION` config).
 * - While the dialog is open, loads the available methods and starts the requested one once per opening.
 * - `restart` starts it again, which brings a fresh response (e.g. a new session token) to its step.
 * - `closeFlow` lets the backend know the player left a started GreenID flow without finishing it.
 *
 * Without a `startMethod` (or when the site does not offer it) `stepId` is `undefined` and nothing runs.
 */
function useKycStartMethod({ isKycDialogOpen, startMethod, startFlow, clearFlow, setHasError }: UseKycStartMethodOptions) {
  const completeGreenId = useGreenIdComplete();
  const hasStartedMethodRef = useRef(false);

  const stepId = startMethod ? getSiteConfig().KYC_VERIFICATION.methods.find(method => method.name === startMethod)?.stepId : undefined;

  // Methods are only needed to resolve the id of the method to start straight away
  const {
    data: availableMethods,
    isError: isMethodsError,
    refetch: refetchMethods,
  } = useApiQuery<KycVerificationMethod[]>(API_ROUTES.PLAYER_KYC.VERIFICATION.GET_METHODS, {
    queryOptions: { enabled: isKycDialogOpen && !!stepId },
  });

  const findMethodId = (methods?: KycVerificationMethod[] | null) => methods?.find(method => method.name === startMethod)?.id;
  const methodId = findMethodId(availableMethods);

  const failUnavailable = () => {
    debug.forceError('[KYC Dialog] Start method not available:', startMethod);
    setHasError(true);
  };

  const failUnavailableOnStart = useEffectEvent(failUnavailable);

  const failMethodsLoad = useEffectEvent(() => {
    debug.forceError('[KYC Dialog] Failed to load the verification methods');
    setHasError(true);
  });

  const startRequestedFlow = useEffectEvent((id: string) => {
    void startFlow(id);
  });

  const restart = async () => {
    setHasError(false);
    clearFlow();

    const id = methodId ?? findMethodId((await refetchMethods()).data);
    if (!id) {
      failUnavailable();
      return;
    }

    await startFlow(id);
  };

  // Starts the requested method once its id is known, once per dialog opening
  useEffect(() => {
    if (!isKycDialogOpen || !stepId) {
      hasStartedMethodRef.current = false;
      return;
    }
    if (isMethodsError) {
      failMethodsLoad();
      return;
    }
    if (!availableMethods || hasStartedMethodRef.current) return;

    hasStartedMethodRef.current = true;
    if (!methodId) {
      failUnavailableOnStart();
      return;
    }
    startRequestedFlow(methodId);
  }, [isKycDialogOpen, stepId, availableMethods, isMethodsError, methodId]);

  // Fire and forget: errors are logged by `useGreenIdComplete`
  const closeFlow = (operationId?: string) => {
    if (startMethod !== 'greenid-fallback' || !operationId) return;
    void completeGreenId(operationId, true);
  };

  return { stepId, restart, closeFlow };
}

export { useKycStartMethod };
